import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { APP_NAME, getCurrentYear } from '../../../../../config/branding';
import { ShellSideNavItem } from './shellSideNav';

/**
 * Posts-styled shell sidebar with configurable nav items.
 *
 * @param {Object} props
 * @param {Array} props.items - { key, label, path?, onClick?, icon?, isActive? }
 * @param {boolean} [props.isNavCollapsed]
 * @param {string} [props.drawerOpenClass]
 * @param {boolean} [props.ariaHidden]
 * @param {string} [props.ariaLabel]
 * @param {() => void} [props.onItemNavigate] - Called after navigation (e.g. close mobile drawer)
 */
export default function AppShellSidebar({
  items = [],
  isNavCollapsed = false,
  drawerOpenClass = '',
  ariaHidden,
  ariaLabel = 'Navigation',
  onItemNavigate,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const isItemActive = (item) => {
    if (typeof item.isActive === 'function') return item.isActive(currentPath);
    if (typeof item.isActive === 'boolean') return item.isActive;
    if (!item.path) return false;
    if (item.path.endsWith('/') && item.path !== '/') {
      return currentPath.startsWith(item.path);
    }
    return currentPath === item.path;
  };

  const handleClick = (item) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.path) {
      navigate(item.path);
    }
    onItemNavigate?.();
  };

  return (
    <div
      className={`support-open-disc-shell-sidebar-col${drawerOpenClass}`}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
    >
      <div className="support-open-disc-shell-sidebar-col-inner">
        <aside className="support-open-disc-shell-sidebar support-open-disc-shell-sidebar--primary">
          <nav className="support-open-disc-shell-side-nav" aria-label={ariaLabel}>
            {items.map((item) => (
              <ShellSideNavItem
                key={item.key}
                label={item.label}
                tooltipText={item.tooltipText || item.label}
                isNavCollapsed={isNavCollapsed}
                className={isItemActive(item) ? ' is-active' : ''}
                onClick={() => handleClick(item)}
                icon={item.icon}
                aria-current={isItemActive(item) ? 'page' : undefined}
              />
            ))}
          </nav>
        </aside>
      </div>
      <footer className="support-open-disc-shell-sidebar-col-footer" aria-label="Site footer">
        <p className="support-open-disc-shell-sidebar-col-copyright">
          © {getCurrentYear()} {APP_NAME} All rights reserved
        </p>
        <Link to="/docs" className="support-open-disc-shell-sidebar-col-doc-link">
          Documentation
        </Link>
      </footer>
    </div>
  );
}
